import { Link, ListItem, Td, Text, Tr, UnorderedList } from "@chakra-ui/react";
import { ReactNode, isValidElement } from "react";

import { removeNullUndefined } from "@nens/shared-utils";

import { getPrettyDate } from "@/utils/get-pretty-time";
import { isValidURL } from "@/utils/is-valid-url";

import { DataItem, MetadataItems } from "../dataset-detail";

// Check if the data can be used for rendering
const renderMetadataValue = (data: DataItem) => {
  if (!data) return null;

  if (data instanceof Date) return getPrettyDate(data);

  if (typeof data === "object") {
    if (isValidElement(data)) return data;

    const { text, url } = (() => {
      if ("_default" in data) return { text: data._default, url: data.link };
      if ("url" in data) return { text: data.name, url: data.url };
      return { text: null, url: null };
    })();

    if (url && isValidURL(url))
      return (
        <Link textDecor="underline" isExternal href={url}>
          {text}
        </Link>
      );

    if (text && isValidURL(text))
      return (
        <Link textDecor="underline" isExternal href={text}>
          {text}
        </Link>
      );

    return text;
  }

  if (typeof data === "string") {
    if (isValidURL(data))
      return (
        <Link textDecor="underline" isExternal href={data}>
          {data}
        </Link>
      );
    return data;
  }
};

const renderList: (list: ReactNode[]) => ReactNode = (list) => {
  if (!list.length) return null;
  if (list.length === 1) return list[0];

  return (
    <UnorderedList>
      {list.map((item, index) => (
        <ListItem key={index}>{item}</ListItem>
      ))}
    </UnorderedList>
  );
};

interface MetadataTableRowProps {
  data: string | ReactNode;
  label: ReactNode;
}

const MetadataTableRow = ({ data, label }: MetadataTableRowProps) => {
  return (
    <Tr>
      <Td verticalAlign="top" p={0} py={4} pt={0} pr={2}>
        <Text as="strong">{label}</Text>
      </Td>
      <Td whiteSpace="normal" p={0} py={4} pt={0}>
        {data}
      </Td>
    </Tr>
  );
};

export const MetadataItemsList = ({ metadataItems }: { metadataItems: MetadataItems }) => {
  return metadataItems.map(({ label, data }, itemIndex) => {
    if (!data) return null;

    const renderedData = Array.isArray(data)
      ? renderList(removeNullUndefined(data.map(renderMetadataValue)))
      : renderMetadataValue(data);

    if (!renderedData) return null;

    return (
      <MetadataTableRow data={renderedData} key={`meta-dataitem-${itemIndex}`} label={label} />
    );
  });
};
